import { useEffect, useRef, useState } from "react";
import { Gavel, Plus, Trash2, Sparkles, ExternalLink, Search, PackageCheck, X, Upload, ChevronDown, RefreshCw, Camera } from "lucide-react";
import { toast } from "sonner";
import api, { formatCurrency, wholeDollarHint } from "@/lib/api";

const STATUS_TABS = [
  { id: "watch", label: "Acompanhando" },
  { id: "bought", label: "Comprados" },
  { id: "in_stock", label: "No estoque" },
  { id: "lost", label: "Perdidos" },
];

// Ordem natural por linha: letras primeiro (A, B... HN) depois número (A1, A2, F15).
// Usa run_number como desempate. Carros sem linha vão pro fim.
const laneParts = (c) => {
  const lane = `${c.lane || ""}`.trim().toUpperCase();
  const run = `${c.run_number || ""}`.trim().toUpperCase();
  const alpha = (lane.match(/[A-Z]+/) || [""])[0];
  const ld = lane.replace(/\D/g, "");
  const rd = run.replace(/\D/g, "");
  return {
    has: !!lane,
    alpha,
    laneNum: ld ? parseInt(ld, 10) : NaN,
    runNum: rd ? parseInt(rd, 10) : NaN,
  };
};
const laneCmp = (a, b) => {
  const pa = laneParts(a);
  const pb = laneParts(b);
  if (pa.has !== pb.has) return pa.has ? -1 : 1;
  const aNumOnly = !pa.alpha;
  const bNumOnly = !pb.alpha;
  if (aNumOnly !== bNumOnly) return aNumOnly ? 1 : -1;
  if (pa.alpha !== pb.alpha) return pa.alpha < pb.alpha ? -1 : 1;
  const la = Number.isNaN(pa.laneNum) ? Infinity : pa.laneNum;
  const lb = Number.isNaN(pb.laneNum) ? Infinity : pb.laneNum;
  if (la !== lb) return la - lb;
  const ra = Number.isNaN(pa.runNum) ? Infinity : pa.runNum;
  const rb = Number.isNaN(pb.runNum) ? Infinity : pb.runNum;
  return ra - rb;
};

export default function AuctionPage({ t }) {
  const [cars, setCars] = useState([]);
  const [statusTab, setStatusTab] = useState("watch");
  const [search, setSearch] = useState("");
  const [laneFilter, setLaneFilter] = useState("all");
  const [auctionFilter, setAuctionFilter] = useState("all");
  const [vins, setVins] = useState("");
  const [auctionName, setAuctionName] = useState("");
  const [auctionDate, setAuctionDate] = useState("");
  const [adding, setAdding] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [scanning, setScanning] = useState(false);
  const cameraRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [fileAuction, setFileAuction] = useState("");
  const fileRef = useRef(null);
  const [showImport, setShowImport] = useState(false);
  const [openId, setOpenId] = useState(null);
  const addingRef = useRef(false);
  const [batch, setBatch] = useState(null);
  const pollRef = useRef(null);
  const vinCount = new Set((vins.toUpperCase().match(/\b[A-HJ-NPR-Z0-9]{17}\b/g) || []).filter((v) => /\d/.test(v))).size;

  const pollBatch = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get("/auction-cars/analyze-all/status");
        setBatch(data);
        if (data.status !== "running") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (data.status === "done") {
            toast.success(`Análise em lote concluída: ${data.done - (data.failed || 0)} ok${data.failed ? ` · ${data.failed} falharam` : ""}`);
            setBatch(null);
            reload();
          }
        }
      } catch { /* mantém polling */ }
    }, 3000);
  };

  useEffect(() => {
    api.get("/auction-cars/analyze-all/status").then((r) => {
      if (r.data?.status === "running") { setBatch(r.data); pollBatch(); }
    }).catch(() => {});
    return () => pollRef.current && clearInterval(pollRef.current);
  }, []); // eslint-disable-line

  const pendingAnalysis = cars.filter((c) => c.status === "watch" && !(c.ai_analysis?.analysis)).length;

  const analyzeAll = async () => {
    if (!window.confirm(`Analisar ${pendingAnalysis} carro(s) com IA de uma vez? (usa créditos — só os que ainda não têm análise)`)) return;
    try {
      const { data } = await api.post("/auction-cars/analyze-all");
      setBatch({ status: "running", total: data.total, done: 0 });
      toast.success(`Analisando ${data.total} carros em segundo plano...`);
      pollBatch();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao iniciar a análise em lote");
      if (err.response?.status === 409) pollBatch();
    }
  };

  const reload = () => api.get("/auction-cars").then((r) => setCars(r.data || [])).catch(() => {});
  useEffect(() => { reload(); }, []);
  useEffect(() => { setLaneFilter("all"); setAuctionFilter("all"); }, [statusTab]);

  const addCars = async (e) => {
    e.preventDefault();
    await submitImport(vins, auctionName, () => setVins(""));
  };

  const submitImport = async (text, name, onDone) => {
    if (addingRef.current) return;
    if (!text || !text.trim()) { toast.error("Cole a lista, uma URL ou anexe o arquivo primeiro"); return; }
    addingRef.current = true;
    setAdding(true);
    try {
      const { data } = await api.post("/auction-cars", { vins: text, auction_name: name, auction_date: auctionDate });
      toast.success(`${data.created.length} carro(s) adicionado(s)${data.skipped ? ` · ${data.skipped} já estavam na lista` : ""}`);
      if (onDone) onDone();
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao adicionar");
    } finally { addingRef.current = false; setAdding(false); }
  };

  const handleFile = async (f, name) => {
    if (!f || addingRef.current) return;
    addingRef.current = true;
    setAdding(true);
    toast.info(`Lendo "${f.name}"...`);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("auction_name", name || "");
      fd.append("auction_date", auctionDate || "");
      const { data } = await api.post("/auction-cars/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const parts = [];
      if (data.created?.length) parts.push(`${data.created.length} carro(s) novo(s)`);
      if (data.updated) parts.push(`${data.updated} atualizado(s) com milhas/leilão/MMR do arquivo`);
      toast.success(parts.length ? parts.join(" · ") : "Nada novo no arquivo");
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao importar o arquivo");
    } finally { addingRef.current = false; setAdding(false); }
  };

  const scanSticker = async (file) => {
    setScanning(true);
    toast.info("Lendo a foto do sticker...");
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (auctionFilter !== "all") fd.append("auction_name", auctionFilter);
      const { data } = await api.post("/auction-cars/scan-sticker", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const c = data.car || {};
      const nome = [c.year, c.make, c.model].filter(Boolean).join(" ") || data.scanned?.vin;
      toast.success(data.created ? `${nome} adicionado na lista!` : `${nome} já estava — dados atualizados`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não consegui ler a foto — tenta de novo");
    } finally { setScanning(false); }
  };

  const syncEdge = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/auction-cars/sync-edge");
      if (data.nothing_new || (data.created?.length === 0 && !data.skipped)) {
        toast.success("Watch List do EDGE em dia — nenhum carro novo");
      } else {
        toast.success(`${data.created.length} carro(s) novo(s) do EDGE${data.skipped ? ` · ${data.skipped} já estavam na lista` : ""}`);
      }
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao sincronizar o EDGE");
    } finally { setSyncing(false); }
  };

  const clearList = async () => {
    const scope = auctionFilter === "all" ? "TODA a lista desta aba" : `os carros do leilão "${auctionFilter}"`;
    const qty = auctionFilter === "all" ? inTab.length : inAuction.length;
    if (!window.confirm(`Apagar ${scope} (${qty} carro(s))? Não dá pra desfazer.`)) return;
    try {
      const { data } = await api.delete("/auction-cars", { params: { status: statusTab, ...(auctionFilter !== "all" ? { auction_name: auctionFilter } : {}) } });
      toast.success(`${data.deleted} carro(s) removido(s) da lista`);
      setAuctionFilter("all");
      setLaneFilter("all");
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao limpar a lista");
    }
  };

  const counts = STATUS_TABS.reduce((acc, s) => ({ ...acc, [s.id]: cars.filter((c) => c.status === s.id).length }), {});
  const inTab = cars.filter((c) => c.status === statusTab);
  const auctionOptions = [...new Set(inTab.map((c) => (c.auction_name || "").trim()).filter(Boolean))].sort();
  const inAuction = inTab.filter((c) => auctionFilter === "all" || (c.auction_name || "").trim() === auctionFilter);
  const laneOptions = [...new Set(inAuction.map((c) => (c.lane || "").trim()).filter(Boolean))].sort((a, b) => laneCmp({ lane: a }, { lane: b }));
  const shown = inAuction
    .filter((c) => laneFilter === "all" || (c.lane || "").trim() === laneFilter)
    .filter((c) => {
      if (!search) return true;
      const f = search.toLowerCase();
      return `${c.year} ${c.make} ${c.model} ${c.trim} ${c.vin} ${c.lane} ${c.run_number} ${c.auction_name}`.toLowerCase().includes(f);
    })
    .sort(laneCmp);

  return (
    <div data-testid="auction-tab">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight flex items-center gap-2">
            <Gavel className="text-primary" size={22} /> Leilão
          </h2>
          <p className="text-xs text-text-secondary mt-1">Cole os VINs da run list, vistorie no leilão, puxe o MMR e deixe a IA fazer o levantamento do carro</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="auction-scan-sticker"
            onClick={() => cameraRef.current?.click()}
            disabled={scanning}
            className="border border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10 disabled:opacity-50 px-4 h-10 text-[10px] font-display font-black uppercase tracking-widest inline-flex items-center gap-1.5 transition-colors"
          >
            <Camera size={13} className={scanning ? "animate-pulse" : ""} /> {scanning ? "Lendo foto..." : "Foto do sticker"}
          </button>
          <input
            ref={cameraRef}
            data-testid="auction-camera-input"
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) scanSticker(f); e.target.value = ""; }}
          />
          <button
            type="button"
            data-testid="auction-sync-edge"
            onClick={syncEdge}
            disabled={syncing}
            className="bg-[#C9A227] hover:opacity-90 disabled:opacity-50 text-black px-4 h-10 text-[10px] font-display font-black uppercase tracking-widest inline-flex items-center gap-1.5 transition-opacity"
          >
            <RefreshCw size={13} className={syncing ? "animate-spin" : ""} /> {syncing ? "Sincronizando..." : "Sincronizar EDGE"}
          </button>
          <button
            type="button"
            data-testid="auction-toggle-import"
            onClick={() => setShowImport((v) => !v)}
            className="md:hidden border border-border text-text-secondary hover:border-primary hover:text-primary px-4 h-10 text-[10px] font-display font-bold uppercase tracking-widest inline-flex items-center gap-1.5 transition-colors"
          >
            <Plus size={13} /> {showImport ? "Fechar" : "Importar"}
          </button>
        </div>
      </div>

      <form onSubmit={addCars} className={`${showImport ? "grid" : "hidden"} md:grid grid-cols-1 border border-border bg-surface/40 p-4 mb-6 md:mb-8 gap-3 md:grid-cols-[1fr_200px_160px_auto]`}>
        <div>
          <textarea
            data-testid="auction-vins"
            rows={3}
            placeholder="Cole a URL da sua Watch List do EDGE (eu entro com a sua conta e puxo os carros sozinho), ou cole o texto da lista (Manheim, ADESA — Ctrl+A, Ctrl+C na página). Eu acho os VINs de 17 caracteres (até 150 carros)"
            value={vins}
            onChange={(e) => setVins(e.target.value)}
            className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 py-2 text-sm"
          />
          {vinCount > 0 && (
            <p data-testid="auction-vin-count" className="text-[10px] text-[#C9A227] font-display font-bold uppercase tracking-wider mt-1">
              ✓ {vinCount} VIN{vinCount > 1 ? "s" : ""} detectado{vinCount > 1 ? "s" : ""}
            </p>
          )}
          <div
            data-testid="auction-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) { setPendingFile(f); setFileAuction(auctionName || ""); } }}
            onClick={() => fileRef.current?.click()}
            className={`mt-2 flex flex-col items-center justify-center text-center gap-1 border-2 border-dashed px-4 py-5 cursor-pointer transition-colors ${dragOver ? "border-[#C9A227] bg-[#C9A227]/10" : "border-border hover:border-primary"}`}
          >
            <Upload size={20} className={dragOver ? "text-[#C9A227]" : "text-text-secondary"} />
            <p className="text-xs font-display font-bold uppercase tracking-wider">Baixou o arquivo do leilão? Solta aqui</p>
            <p className="text-[10px] text-text-secondary">Arraste ou toque pra escolher — aceita PDF, Numbers, Excel, CSV e TXT (Manheim, ADESA, EDGE). Puxo VIN, milhas, lane, run, leilão e MMR</p>
            <input
              ref={fileRef}
              data-testid="auction-file"
              type="file"
              accept=".csv,.txt,.tsv,.numbers,.xlsx,.xlsm,.pdf"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) { setPendingFile(f); setFileAuction(auctionName || ""); } e.target.value = ""; }}
            />
          </div>
        </div>
        <div className="self-end">
          <div className="flex gap-1 mb-1">
            {["Manheim", "ADESA", "EDGE Pipeline"].map((n) => (
              <button key={n} type="button" data-testid={`auction-preset-${n.split(" ")[0].toLowerCase()}`} onClick={() => setAuctionName(n)}
                className={`px-2 h-6 text-[9px] font-display font-bold uppercase tracking-wider border transition-colors ${auctionName === n ? "border-primary text-primary" : "border-border text-text-secondary hover:border-primary"}`}>
                {n}
              </button>
            ))}
          </div>
          <input data-testid="auction-name" type="text" placeholder="Leilão" value={auctionName} onChange={(e) => setAuctionName(e.target.value)} className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm" />
        </div>
        <input data-testid="auction-date" type="date" value={auctionDate} onChange={(e) => setAuctionDate(e.target.value)} className="bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm self-end" />
        <button data-testid="auction-add" type="submit" disabled={adding} className="bg-primary hover:bg-primary-hover disabled:opacity-50 text-white px-5 h-11 font-display font-bold uppercase text-xs tracking-widest inline-flex items-center gap-2 self-end">
          <Plus size={14} /> {adding ? "Decodificando..." : "Adicionar"}
        </button>
      </form>

      {pendingFile && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="auction-file-dialog">
          <div className="bg-surface border border-border w-full max-w-md p-5 space-y-4">
            <div>
              <h3 className="font-display font-black uppercase tracking-tight text-lg">De qual leilão é esse arquivo?</h3>
              <p className="text-[11px] text-text-secondary mt-1 font-mono truncate">📄 {pendingFile.name}</p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {[...new Set([...cars.map((c) => (c.auction_name || "").trim()).filter(Boolean), "Manheim", "ADESA", "EDGE Pipeline"])].sort().map((n) => (
                <button key={n} type="button" onClick={() => setFileAuction(n)}
                  className={`px-2.5 h-8 text-[10px] font-display font-bold uppercase tracking-wider border transition-colors ${fileAuction === n ? "border-[#C9A227] text-[#C9A227] bg-[#C9A227]/10" : "border-border text-text-secondary hover:border-primary"}`}>
                  {n}
                </button>
              ))}
            </div>
            <input
              data-testid="auction-file-auction-name"
              type="text"
              placeholder="Ou digite o nome do leilão..."
              value={fileAuction}
              onChange={(e) => setFileAuction(e.target.value)}
              className="w-full bg-background border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
            />
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                data-testid="auction-file-import"
                onClick={() => { const f = pendingFile; setPendingFile(null); handleFile(f, fileAuction.trim()); }}
                className="flex-1 bg-primary hover:bg-primary-hover text-white h-12 text-[11px] font-display font-black uppercase tracking-widest"
              >
                Importar{fileAuction.trim() ? ` · ${fileAuction.trim()}` : ""}
              </button>
              <button
                data-testid="auction-file-import-auto"
                onClick={() => { const f = pendingFile; setPendingFile(null); handleFile(f, ""); }}
                className="flex-1 border border-border hover:border-primary hover:text-primary h-12 text-[10px] font-display font-bold uppercase tracking-widest transition-colors"
              >
                Usar o nome que vem no arquivo
              </button>
            </div>
            <button data-testid="auction-file-cancel" onClick={() => setPendingFile(null)} className="w-full text-[10px] uppercase tracking-widest text-text-secondary hover:text-brand transition-colors">Cancelar</button>
          </div>
        </div>
      )}

      <div className="sticky top-14 lg:static z-20 bg-background py-2 lg:py-0 flex flex-wrap items-center gap-2 mb-4 lg:mb-5">
        <div className="flex gap-2 overflow-x-auto w-full md:w-auto pb-1 md:pb-0 [-webkit-overflow-scrolling:touch]">
          {STATUS_TABS.map((s) => (
            <button key={s.id} data-testid={`auction-filter-${s.id}`} onClick={() => setStatusTab(s.id)}
              className={`shrink-0 px-4 h-10 lg:h-9 text-[11px] font-display font-bold uppercase tracking-widest border transition-colors ${statusTab === s.id ? "bg-primary border-primary text-white" : "border-border text-text-secondary hover:border-primary"}`}>
              {s.label} ({counts[s.id] || 0})
            </button>
          ))}
        </div>
        {auctionOptions.length > 1 && (
          <select
            data-testid="auction-auction-filter"
            value={auctionFilter}
            onChange={(e) => { setAuctionFilter(e.target.value); setLaneFilter("all"); }}
            className="bg-surface border border-border focus:border-primary focus:outline-none px-3 h-10 lg:h-9 text-xs font-display font-bold uppercase tracking-wider w-full md:w-auto"
          >
            <option value="all">Todos os leilões ({inTab.length})</option>
            {auctionOptions.map((an) => (
              <option key={an} value={an}>{an} ({inTab.filter((c) => (c.auction_name || "").trim() === an).length})</option>
            ))}
          </select>
        )}
        {laneOptions.length > 0 && (
          <select
            data-testid="auction-lane-filter"
            value={laneFilter}
            onChange={(e) => setLaneFilter(e.target.value)}
            className="bg-surface border border-border focus:border-primary focus:outline-none px-3 h-10 lg:h-9 text-xs font-display font-bold uppercase tracking-wider w-full md:w-auto"
          >
            <option value="all">Todas as linhas ({inAuction.length})</option>
            {laneOptions.map((ln) => (
              <option key={ln} value={ln}>Linha {ln} ({inAuction.filter((c) => (c.lane || "").trim() === ln).length})</option>
            ))}
          </select>
        )}
        <div className="border border-border flex items-center px-3 h-10 lg:h-9 w-full md:w-auto md:ml-auto md:min-w-[200px]">
          <Search size={13} className="text-text-secondary mr-2" />
          <input data-testid="auction-search" type="text" placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)} className="bg-transparent w-full focus:outline-none text-xs" />
        </div>
        {batch?.status === "running" ? (
          <div data-testid="auction-batch-progress" className="flex items-center gap-2 border border-[#C9A227] px-3 h-9 min-w-[220px]">
            <div className="flex-1 h-1.5 bg-border overflow-hidden">
              <div className="h-full bg-[#C9A227] transition-all" style={{ width: `${batch.total ? Math.round(((batch.done || 0) / batch.total) * 100) : 5}%` }} />
            </div>
            <span className="text-[10px] font-display font-bold text-[#C9A227] whitespace-nowrap">{batch.done || 0}/{batch.total} analisados</span>
          </div>
        ) : pendingAnalysis > 0 && (
          <button data-testid="auction-analyze-all" onClick={analyzeAll}
            className="bg-[#C9A227] hover:opacity-90 text-black px-4 h-9 text-[10px] font-display font-black uppercase tracking-widest inline-flex items-center gap-1.5">
            <Sparkles size={12} /> Analisar todos ({pendingAnalysis})
          </button>
        )}
        {inTab.length > 0 && (
          <button data-testid="auction-clear-list" onClick={clearList}
            className="border border-red-800/60 text-red-400 hover:bg-red-950/40 px-4 h-10 lg:h-9 text-[10px] font-display font-black uppercase tracking-widest inline-flex items-center justify-center gap-1.5 w-full md:w-auto">
            <Trash2 size={12} /> Limpar lista{auctionFilter !== "all" ? " (leilão filtrado)" : ""}
          </button>
        )}
      </div>

      {shown.length === 0 ? (
        <p className="text-text-secondary text-sm text-center py-16">Nenhum carro aqui ainda</p>
      ) : (
        <div className="space-y-3 lg:space-y-4">
          {shown.map((c) => (
            <AuctionCard
              key={`${c.id}-${c.ai_analysis?.created_at || ""}`}
              car={c}
              onChanged={reload}
              open={openId === c.id}
              onToggle={() => setOpenId((prev) => (prev === c.id ? null : c.id))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AuctionCard({ car, onChanged, open, onToggle }) {
  const [form, setForm] = useState({
    mileage: car.mileage || "",
    lane: car.lane || "",
    run_number: car.run_number || "",
    announce: car.announce || "",
    notes: car.notes || "",
    mmr_value: car.mmr_value || "",
  });
  const [recon, setRecon] = useState(car.recon_items || []);
  const [newItem, setNewItem] = useState({ description: "", amount: "" });
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(car.ai_analysis || null);
  const [buyPrice, setBuyPrice] = useState("");
  const [buying, setBuying] = useState(false);
  const busyRef = useRef(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reconTotal = recon.reduce((s, it) => s + (Number(it.amount) || 0), 0);

  const buildPayload = () => ({
    ...form,
    mileage: Number(form.mileage) || 0,
    mmr_value: Math.round(Number(form.mmr_value) || 0),
    recon_items: recon.map((it) => ({ ...it, amount: Math.round(Number(it.amount) || 0) })),
  });

  const persist = async (extra = {}) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setSaving(true);
    try {
      await api.put(`/auction-cars/${car.id}`, { ...buildPayload(), ...extra });
      toast.success("Salvo");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    } finally { busyRef.current = false; setSaving(false); }
  };

  const addRecon = () => {
    if (!newItem.description || !Number(newItem.amount)) return;
    setRecon((r) => [...r, { id: crypto.randomUUID(), description: newItem.description, amount: Math.round(Number(newItem.amount)), category: "other" }]);
    setNewItem({ description: "", amount: "" });
  };

  const analyze = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setAnalyzing(true);
    try {
      await api.put(`/auction-cars/${car.id}`, buildPayload());
      const { data } = await api.post(`/auction-cars/${car.id}/analyze`);
      setAnalysis(data);
      toast.success("Análise pronta");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "A análise falhou — tenta de novo");
    } finally { busyRef.current = false; setAnalyzing(false); }
  };

  const markBought = async () => {
    const p = Math.round(Number(buyPrice));
    if (!p || p <= 0) { toast.error("Informe o valor do lance vencedor"); return; }
    setBuying(true);
    try {
      await api.put(`/auction-cars/${car.id}`, { status: "bought", bought_price: p });
      toast.success("Carro marcado como comprado 🎉");
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    finally { setBuying(false); }
  };

  const markLost = async () => {
    try {
      await api.put(`/auction-cars/${car.id}`, { status: "lost" });
      toast.success("Marcado como perdido");
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const toInventory = async () => {
    try {
      await api.post(`/auction-cars/${car.id}/to-inventory`);
      toast.success("Carro mandado pro estoque com os gastos lançados!");
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const remove = async () => {
    if (!window.confirm("Remover esse carro da lista do leilão?")) return;
    try {
      await api.delete(`/auction-cars/${car.id}`);
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao remover"); }
  };

  const title = `${car.year || ""} ${car.make} ${car.model} ${car.trim}`.trim() || car.vin;
  const runShort = (() => {
    let r = `${car.run_number || ""}`.trim();
    if (!r) return "";
    r = r.replace(/^[A-Za-z]+[-\s]?/, "");
    return (r.replace(/^0+/, "") || r) || car.run_number;
  })();
  const demand = analysis?.demand || "";
  const demandClr = demand === "alta" ? "text-success border-success" : demand === "baixa" ? "text-brand border-brand" : "text-warning border-warning";

  return (
    <div data-testid={`auction-car-${car.id}`} className="border border-border bg-surface/30">
      <div
        data-testid={`auction-head-${car.id}`}
        onClick={() => window.innerWidth < 1024 && onToggle()}
        className="px-4 py-3 border-b border-border flex flex-wrap items-center gap-3 lg:cursor-default cursor-pointer select-none"
      >
        <ChevronDown size={16} className={`lg:hidden shrink-0 text-text-secondary transition-transform ${open ? "rotate-180" : ""}`} />
        {(car.lane || car.run_number || car.grade) && (
          <div data-testid={`auction-lanerun-${car.id}`} className="shrink-0 flex items-stretch border-2 border-[#C9A227] divide-x-2 divide-[#C9A227]/30">
            {car.lane && (
              <div className="px-3 py-1 text-center bg-[#C9A227]/15">
                <p className="text-[8px] font-display font-bold uppercase tracking-widest text-[#C9A227]/80 leading-none">Linha</p>
                <p className="font-display font-black text-3xl lg:text-2xl text-[#C9A227] leading-tight">{car.lane}</p>
              </div>
            )}
            {runShort && (
              <div className="px-3 py-1 text-center">
                <p className="text-[8px] font-display font-bold uppercase tracking-widest text-text-secondary leading-none">Run</p>
                <p className="font-display font-black text-3xl lg:text-2xl leading-tight">{runShort}</p>
              </div>
            )}
            {car.grade && (
              <div className="px-3 py-1 text-center">
                <p className="text-[8px] font-display font-bold uppercase tracking-widest text-text-secondary leading-none">Grade</p>
                <p data-testid={`auction-grade-${car.id}`} className="font-display font-black text-3xl lg:text-2xl leading-tight" style={{ color: Number(car.grade) >= 4 ? "#16a34a" : Number(car.grade) >= 3 ? "#C9A227" : "#dc2626" }}>{car.grade}</p>
              </div>
            )}
          </div>
        )}
        <div className="flex-1 min-w-[160px]">
          <p className="font-display font-black uppercase tracking-tight">{title}</p>
          <p className="text-[10px] font-mono text-text-secondary mt-0.5">
            VIN {car.vin}
            {car.auction_name && <span> · {car.auction_name}</span>}
            {car.auction_date && <span> · {car.auction_date}</span>}
          </p>
        </div>
        {analysis?.price_high > 0 && (
          <div className="text-right">
            <p className="text-[9px] uppercase tracking-widest text-text-secondary">{analysis.range_source ? "Faixa de venda (anúncios reais)" : "Faixa de venda (IA)"}</p>
            <p data-testid={`auction-range-${car.id}`} className="font-display font-black text-xl text-[#C9A227]">{formatCurrency(analysis.price_low)} – {formatCurrency(analysis.price_high)}</p>
          </div>
        )}
        {car.status === "bought" && (
          <span className="text-[10px] font-display font-bold uppercase tracking-wider px-2 py-1 border border-success text-success">Comprado · {formatCurrency(car.bought_price)}</span>
        )}
        {car.status === "in_stock" && (
          <span className="text-[10px] font-display font-bold uppercase tracking-wider px-2 py-1 border border-primary text-primary">No estoque</span>
        )}
        <button data-testid={`auction-del-${car.id}`} onClick={(e) => { e.stopPropagation(); remove(); }} className="w-9 h-9 lg:w-8 lg:h-8 border border-border hover:border-brand hover:text-brand flex items-center justify-center transition-colors"><Trash2 size={13} /></button>
      </div>

      <div className={`${open ? "grid" : "hidden"} lg:grid grid-cols-1 p-4 gap-4 lg:grid-cols-2`}>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <Field label="Milhas" type="number" value={form.mileage} set={(v) => set("mileage", v)} testid={`auction-miles-${car.id}`} />
            <Field label="Lane" value={form.lane} set={(v) => set("lane", v)} />
            <Field label="Run #" value={form.run_number} set={(v) => set("run_number", v)} />
          </div>
          <Field label="Anúncios / condição no leilão" value={form.announce} set={(v) => set("announce", v)} placeholder="Ex: AS-IS, luz de motor, pintura porta..." />
          <Field label="Observações da vistoria" value={form.notes} set={(v) => set("notes", v)} placeholder="O que você viu no carro" />

          <div className="border border-border p-3">
            <p className="label-eyebrow mb-2">Gastos estimados de recuperação · <span className="text-warning font-bold">{formatCurrency(reconTotal)}</span></p>
            {recon.map((it, i) => (
              <div key={it.id || i} className="flex items-center gap-2 text-xs py-1 border-b border-border/50">
                <span className="flex-1">{it.description}</span>
                <span className="font-display font-bold">{formatCurrency(it.amount)}</span>
                <button type="button" onClick={() => setRecon((r) => r.filter((_, j) => j !== i))} className="text-text-secondary hover:text-brand"><X size={12} /></button>
              </div>
            ))}
            <div className="flex gap-2 mt-2">
              <input data-testid={`auction-recon-desc-${car.id}`} type="text" placeholder="Ex: Pneus, freios..." value={newItem.description} onChange={(e) => setNewItem((n) => ({ ...n, description: e.target.value }))} className="flex-1 bg-surface border border-border focus:border-primary focus:outline-none px-2 h-9 text-xs" />
              <input data-testid={`auction-recon-amount-${car.id}`} type="number" placeholder="US$" value={newItem.amount} onChange={(e) => setNewItem((n) => ({ ...n, amount: e.target.value }))} className="w-24 bg-surface border border-border focus:border-primary focus:outline-none px-2 h-9 text-xs" />
              <button type="button" data-testid={`auction-recon-add-${car.id}`} onClick={addRecon} className="border border-primary text-primary hover:bg-primary hover:text-white px-3 h-9 text-xs transition-colors"><Plus size={13} /></button>
            </div>
            {wholeDollarHint(newItem.amount) && <p className="text-[10px] text-warning mt-1">⚠ {wholeDollarHint(newItem.amount)}</p>}
          </div>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2 items-end">
            <div>
              <Field label="MMR (US$)" type="number" value={form.mmr_value} set={(v) => set("mmr_value", v)} testid={`auction-mmr-${car.id}`} />
              {Number(car.pmr_value) > 0 && Math.round(Number(form.mmr_value)) === Math.round(Number(car.pmr_value)) && (
                <p data-testid={`auction-pmr-note-${car.id}`} className="text-[10px] text-[#C9A227] mt-1">✓ Preenchido com o mercado do EDGE (PMR). Confirme o MMR real na Manheim →</p>
              )}
              {wholeDollarHint(form.mmr_value) && <p className="text-[10px] text-warning mt-1">⚠ {wholeDollarHint(form.mmr_value)}</p>}
            </div>
            <button
              type="button"
              data-testid={`auction-open-mmr-${car.id}`}
              onClick={() => window.open(`https://mmr.manheim.com/?country=US&vin=${car.vin}${Math.round(Number(form.mileage)) > 0 ? `&mileage=${Math.round(Number(form.mileage))}` : ""}`, "_blank")}
              className="border border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10 h-11 text-[10px] font-display font-bold uppercase tracking-widest inline-flex flex-col items-center justify-center gap-0.5 transition-colors"
            >
              <span className="inline-flex items-center gap-1.5"><ExternalLink size={12} /> Abrir MMR na Manheim</span>
              {car.grade && <span className="text-[9px] normal-case tracking-normal font-bold" style={{ color: Number(car.grade) >= 4 ? "#16a34a" : Number(car.grade) >= 3 ? "#C9A227" : "#dc2626" }}>milhas já vão no link · marque Grade {car.grade}</span>}
            </button>
          </div>
          <div className="flex gap-2">
            <button data-testid={`auction-save-${car.id}`} onClick={() => persist()} disabled={saving} className="flex-1 border border-border hover:border-primary hover:text-primary disabled:opacity-50 h-12 lg:h-10 text-[10px] font-display font-bold uppercase tracking-widest transition-colors">
              {saving ? "Salvando..." : "Salvar"}
            </button>
            <button data-testid={`auction-analyze-${car.id}`} onClick={analyze} disabled={analyzing} className="flex-1 bg-primary hover:bg-primary-hover disabled:opacity-50 text-white h-12 lg:h-10 text-[10px] font-display font-bold uppercase tracking-widest inline-flex items-center justify-center gap-1.5">
              <Sparkles size={12} /> {analyzing ? "Analisando..." : "Analisar com IA"}
            </button>
          </div>

          {analysis?.analysis && (
            <div data-testid={`auction-analysis-${car.id}`} className="border border-[#C9A227]/40 bg-[#C9A227]/[0.04] p-3 space-y-2">
              {analysis.confidence && (
                <div
                  data-testid={`auction-confidence-${car.id}`}
                  className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] px-2 py-1.5 border"
                  style={analysis.confidence === "alta"
                    ? { borderColor: "#16a34a", color: "#16a34a", backgroundColor: "rgba(22,163,74,0.08)" }
                    : analysis.confidence === "média"
                      ? { borderColor: "#d97706", color: "#d97706", backgroundColor: "rgba(217,119,6,0.08)" }
                      : { borderColor: "#dc2626", color: "#dc2626", backgroundColor: "rgba(220,38,38,0.08)" }}
                >
                  <span className="font-display font-black uppercase tracking-widest">● Confiança {analysis.confidence}</span>
                  <span className="text-text-secondary">{analysis.confidence_reason}</span>
                </div>
              )}
              {analysis.max_bid > 0 && (
                <div data-testid={`auction-maxbid-${car.id}`} className="px-3 py-2 border-2" style={{ borderColor: "#C9A227", backgroundColor: "rgba(201,162,39,0.12)" }}>
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-[9px] font-display font-bold uppercase tracking-widest text-[#C9A227]">🎯 Pague até (teto sem prejuízo)</p>
                      <p className="font-display font-black text-2xl leading-tight text-[#C9A227]">{formatCurrency(analysis.max_bid)}</p>
                    </div>
                    <p className="text-[9px] text-text-secondary text-right leading-relaxed">
                      venda rápida {formatCurrency(analysis.quick_sell_price)}<br />
                      − gastos {formatCurrency(analysis.recon_used || 0)} · acima disso não sobra nada
                    </p>
                  </div>
                  {analysis.max_bid_note && (
                    <p data-testid={`auction-maxbid-note-${car.id}`} className="text-[9px] text-warning mt-1">⚠ {analysis.max_bid_note}</p>
                  )}
                </div>
              )}
              {analysis.quick_sell_price > 0 && (
                <div data-testid={`auction-quicksell-${car.id}`} className="flex items-center justify-between gap-2 bg-success/15 border border-success px-3 py-2" style={{ backgroundColor: "rgba(22,163,74,0.12)", borderColor: "#16a34a" }}>
                  <div>
                    <p className="text-[9px] font-display font-bold uppercase tracking-widest text-success" style={{ color: "#16a34a" }}>⚡ Preço pra vender rápido</p>
                    <p className="font-display font-black text-2xl leading-tight" style={{ color: "#16a34a" }}>{formatCurrency(analysis.quick_sell_price)}</p>
                  </div>
                  {analysis.quick_sell_days > 0 && (
                    <div className="text-right">
                      <p className="text-[9px] uppercase tracking-widest text-text-secondary">gira em</p>
                      <p className="font-display font-black text-lg" style={{ color: "#16a34a" }}>~{Math.round(analysis.quick_sell_days)} dias</p>
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-[9px] uppercase tracking-widest text-text-secondary">Venda (lojas)</p>
                  <p data-testid={`auction-retail-${car.id}`} className="font-display font-black text-base text-[#C9A227]">
                    {analysis.price_high > 0
                      ? `${formatCurrency(analysis.price_low)} – ${formatCurrency(analysis.price_high).replace(/^US\$\s?/, "")}`
                      : formatCurrency(analysis.market?.avg_price || analysis.est_retail)}
                  </p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-widest text-text-secondary">Marketplace (particular)</p>
                  <p data-testid={`auction-mp-${car.id}`} className="font-display font-black text-base">
                    {analysis.mp_high > 0 ? `${formatCurrency(analysis.mp_low)} – ${formatCurrency(analysis.mp_high).replace(/^US\$\s?/, "")}` : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-widest text-text-secondary">Pátio</p>
                  <p className="font-display font-black text-base">
                    {(analysis.market?.avg_dom > 0 || analysis.est_dom > 0) ? `~${Math.round(analysis.market?.avg_dom || analysis.est_dom)} dias` : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-widest text-text-secondary">Procura</p>
                  {demand ? (
                    <p data-testid={`auction-demand-${car.id}`} className={`font-display font-black text-base uppercase ${demandClr.split(" ")[0]}`}>
                      {demand}{analysis.turnover ? <span className="text-[10px] font-bold text-text-secondary normal-case"> · {analysis.turnover}</span> : null}
                    </p>
                  ) : <p className="font-display font-black text-base">—</p>}
                </div>
              </div>
              {(analysis.market?.listings?.length || 0) > 0 ? (
                <div data-testid={`auction-market-${car.id}`} className="text-[10px] text-text-secondary border-t border-border/50 pt-2">
                  <p className="font-bold text-[#C9A227] uppercase tracking-wider mb-1">
                    {analysis.market.count > 0
                      ? <>🏪 {analysis.market.count} anúncios reais — {analysis.market.search_label ? `${analysis.market.search_label}, mais perto primeiro` : "lojas mais perto primeiro (30mi de Everett)"}</>
                      : <>🏪 {analysis.market.listings.length} anúncio(s) ao redor, todos fora da média (encalhados) — giro lento</>}
                  </p>
                  {(analysis.market.listings || []).slice(0, 8).map((li, i) => (
                    <p key={i} className={li.excluded ? "opacity-50" : ""}>
                      · <span className={li.excluded ? "line-through" : ""}>
                        {li.dealer || "Loja"}{li.city ? ` (${li.city}${li.dist > 0 ? `, a ${Math.round(li.dist)}mi` : ""})` : ""} — <span className="font-bold text-text-primary">{formatCurrency(li.price)}</span>
                        {li.trim ? <> · {li.trim}</> : null}
                        {li.miles > 0 && <>, {Math.round(li.miles).toLocaleString("pt-BR")} mi</>}
                        {li.dom > 0 && <>, {Math.round(li.dom)} dias no pátio</>}
                      </span>
                      {li.excluded && li.reason ? <span className="italic"> fora da média: {li.reason}</span> : null}
                      {li.url ? <a data-testid={`auction-listing-link-${car.id}-${i}`} href={li.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#C9A227] font-bold"> abrir anúncio ↗</a> : null}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-[9px] text-text-secondary border-t border-border/50 pt-1.5">Estimativa da IA — adicione a chave MarketCheck pra anúncios reais</p>
              )}
              <div className="flex gap-2 border-t border-border/50 pt-2">
                {[
                  {
                    label: "CarGurus", tid: "cargurus",
                    href: `https://www.cargurus.com/search?zip=02149&distance=30&newUsed=2${car.year ? `&startYear=${car.year}&endYear=${car.year}` : ""}`,
                  },
                  {
                    label: "Carfax", tid: "carfax",
                    href: `https://www.carfax.com/cars-for-sale?zip=02149&radius=30${car.make ? `&make=${encodeURIComponent(car.make)}` : ""}${car.model ? `&model=${encodeURIComponent(car.model)}` : ""}${car.year ? `&year_min=${car.year}&year_max=${car.year}` : ""}`,
                  },
                  {
                    label: "Marketplace", tid: "fbm",
                    href: `https://www.facebook.com/marketplace/boston/search?query=${encodeURIComponent(`${car.year || ""} ${car.make || ""} ${car.model || ""}`.replace(/\s+/g, " ").trim())}${car.year ? `&minYear=${car.year}&maxYear=${car.year}` : ""}`,
                  },
                ].map((s) => (
                  <a
                    key={s.tid}
                    data-testid={`auction-${s.tid}-${car.id}`}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex-1 border border-border text-text-secondary hover:text-[#C9A227] hover:border-[#C9A227] h-8 text-[9px] font-display font-black uppercase tracking-widest inline-flex items-center justify-center gap-1"
                  >
                    <ExternalLink size={10} /> {s.label}
                  </a>
                ))}
              </div>
              {analysis.analysis && !analysis.overview && (
                <p data-testid={`auction-note-${car.id}`} className="text-xs leading-relaxed text-warning border-t border-border/50 pt-2">⚠ {analysis.analysis}</p>
              )}
              {analysis.comps?.sold_count > 0 && (
                <p className="text-[10px] text-text-secondary border-t border-border/50 pt-2">
                  📊 Sua loja: {analysis.comps.sold_count} parecido(s) vendido(s) · média {formatCurrency(analysis.comps.avg_sold_price)}
                  {analysis.comps.avg_days > 0 && <> · {Math.round(analysis.comps.avg_days)} dias de pátio</>}
                </p>
              )}
            </div>
          )}

          {car.status === "watch" && (
            <div className="flex gap-2 pt-1">
              <input data-testid={`auction-buyprice-${car.id}`} type="number" placeholder="Lance vencedor US$" value={buyPrice} onChange={(e) => setBuyPrice(e.target.value)} className="flex-1 min-w-0 bg-surface border border-border focus:border-primary focus:outline-none px-3 h-12 lg:h-10 text-sm lg:text-xs" />
              <button data-testid={`auction-bought-${car.id}`} onClick={markBought} disabled={buying} className="bg-success hover:opacity-90 disabled:opacity-50 text-white px-4 h-12 lg:h-10 text-[10px] font-display font-bold uppercase tracking-widest" style={{ backgroundColor: "#16a34a" }}>Comprei</button>
              <button data-testid={`auction-lost-${car.id}`} onClick={markLost} className="border border-border text-text-secondary hover:border-brand hover:text-brand px-4 h-12 lg:h-10 text-[10px] font-display font-bold uppercase tracking-widest transition-colors">Perdi</button>
            </div>
          )}
          {car.status === "bought" && (
            <button data-testid={`auction-tostock-${car.id}`} onClick={toInventory} className="w-full bg-[#C9A227] hover:opacity-90 text-black h-11 text-[11px] font-display font-black uppercase tracking-widest inline-flex items-center justify-center gap-2">
              <PackageCheck size={14} /> Mandar pro Estoque (com os gastos)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, set, type = "text", placeholder = "", testid }) {
  return (
    <div>
      <label className="label-eyebrow block mb-1.5">{label}</label>
      <input data-testid={testid} type={type} placeholder={placeholder} value={value} onChange={(e) => set(e.target.value)} className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm" />
    </div>
  );
}
